import { FilterFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import EnhancedTable from 'app/molecules/EnhancedTable';
import { EnhancedColumnType } from 'app/molecules/EnhancedTable/types';
import { StatusBadge } from 'app/atoms';
import Button from 'app/atoms/Button';
import { UserRole, UserRoleLabelMap, UserStatus, UserStatusLabelMap } from 'enums/user';
import { User } from './types';

interface UserTableProps {
  users: User[];
  filters: Record<string, string>;
  onFilter: (dataIndex: string, value: string) => void;
  onDeactivate: (user: User) => void;
}

const UserTable: React.FC<UserTableProps> = ({ users, filters, onFilter, onDeactivate }) => {
  const columns: EnhancedColumnType<User>[] = [
    {
      title: 'Name',
      dataIndex: 'displayName',
      key: 'displayName',
      sorter: (a, b) => (a.displayName || '').localeCompare(b.displayName || ''),
      render: (_, user) => user.displayName || <span className="text-gray-400">Pending first sign-in</span>,
      className: 'font-figtree',
      filterOptions: { type: 'search', defaultValue: filters.displayName, Icon: FilterFilled },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
      className: 'font-figtree',
      filterOptions: { type: 'search', defaultValue: filters.email, Icon: FilterFilled },
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: UserRole) => UserRoleLabelMap[role],
      className: 'font-figtree',
      filterOptions: {
        type: 'radio',
        Icon: FilterFilled,
        defaultValue: filters.role,
        options: Object.values(UserRole).map((role) => ({ label: UserRoleLabelMap[role], value: role })),
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: UserStatus) => <StatusBadge status={status} label={UserStatusLabelMap[status]} />,
      filterOptions: {
        type: 'radio',
        Icon: FilterFilled,
        defaultValue: filters.status,
        options: Object.values(UserStatus).map((status) => ({ label: UserStatusLabelMap[status], value: status })),
      },
    },
    {
      title: 'Invited',
      dataIndex: 'invitedAt',
      key: 'invitedAt',
      render: (value?: string) => (value ? dayjs(value).format('MMM D, YYYY HH:mm') : '-'),
      sorter: (a, b) => (a.invitedAt || '').localeCompare(b.invitedAt || ''),
      className: 'font-figtree whitespace-nowrap',
    },
    {
      title: '',
      key: 'deactivate',
      align: 'right',
      render: (_, user) =>
        user.status !== UserStatus.DEACTIVATED ? (
          <Button variant="secondary" danger onClick={() => onDeactivate(user)}>
            Deactivate
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="border rounded-lg overflow-x-auto">
      <EnhancedTable
        dataSource={users}
        columns={columns}
        onFilter={(dataIndex, value) => onFilter(String(dataIndex), String(value))}
        rowKey="id"
        showEdit={false}
        showView={false}
        showRemove={false}
        className="font-figtree"
      />
    </div>
  );
};

export default UserTable;
